import { MouseEvent, useState } from "react";
import { Line } from "react-chartjs-2";
import { Replay } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  CardContent,
  IconButton,
  Popover,
  Slider,
  Typography as T,
} from "@mui/material";
import ConceptTitleCard from "catalog/components/ConceptTitleCard";
import { SiteDetails } from "catalog/types/interfaces/conceptResponse";
import { DEFAULT_OPTS } from "catalog/utils/chart";
import { ChartData, ChartOptions } from "chart.js";
import { merge } from "chart.js/helpers";
import FlatCard from "~/components/FlatCard";
import { COLOR_PALETTE } from "~/customer/chartJS/colors";
import BehaviorCodeChart from "./BehaviorCodeChart";
import HistologiesList from "./HistologiesList";
import MeasurementChart from "./MeasurementChart";

/**
 * Number formatters
 */
const compactFormatter = new Intl.NumberFormat("en", { notation: "compact" });

/**
 * Chart options
 */
const CUMULATIVE_CHART_OPTS: ChartOptions<"line"> = merge<
  ChartOptions<"line">,
  ChartOptions<"line">
>(
  {
    scales: {
      x: {
        title: {
          display: true,
          text: "Diagnosis Year",
        },
      },
      y: {
        title: {
          display: true,
          text: "Participant Count",
        },
        grid: { display: false },
        ticks: {
          callback: (value) => {
            if (!value) {
              return "";
            }

            if (!isNaN(Number(value))) {
              return compactFormatter.format(Number(value));
            }
            return value;
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        yAlign: "center",
      },
    },
    elements: {
      line: {
        tension: 0.1,
        fill: true,
      },
      point: {
        radius: 0,
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  },
  DEFAULT_OPTS
);

/**
 * Helper functions
 */
const transformCumulativeData = (
  data: SiteDetails["distinct_counts_over_time"],
  filterBy?: number[]
): ChartData<"line"> => {
  const yearlyData: { [year: string]: number } = {};

  data.forEach((point) => {
    const year = new Date(point.start_date).getUTCFullYear().toString();
    point.histology_counts.forEach((histology) => {
      const [, count] = Object.entries(histology)[0];
      if (!yearlyData[year]) {
        yearlyData[year] = count;
      } else {
        yearlyData[year] += count;
      }
    });
  });

  let cumulativeTotal = 0;

  const filteredData = Object.entries(yearlyData).filter((v) => {
    const year = Number(v[0]);
    return !filterBy || (year >= filterBy[0] && year <= filterBy[1]);
  });

  const cumulativeData = Object.entries(yearlyData).map((entry) => {
    cumulativeTotal += entry[1];
    return cumulativeTotal;
  });

  return {
    labels: filteredData.sort().map((year) => year[0]),
    datasets: [
      {
        label: "Cumulative Diagnoses",
        data: cumulativeData,
        borderColor: COLOR_PALETTE[0],
        borderWidth: 2,
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) {
            return;
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, alpha(COLOR_PALETTE[0], 0.65));
          gradient.addColorStop(1, alpha(COLOR_PALETTE[0], 0.0));
          return gradient;
        },
      },
    ],
  };
};

/**
 * Initial state
 */

const DiagnosisRangeMenu = ({
  data,
  onRangeChange,
}: {
  data: SiteDetails["distinct_counts_over_time"];
  onRangeChange: (range: number[]) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  /**
   * Calculate range bounds
   */
  const startYear = new Date(data[0].start_date).getUTCFullYear();
  const endYear = new Date(data[data.length - 1].start_date).getUTCFullYear();
  const [range, setRange] = useState([startYear, endYear]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleChange = (_: Event, newValue: number | number[]) => {
    setRange(newValue as number[]);
  };

  const handleChangeCommitted = () => {
    onRangeChange(range);
  };

  const handleReset = () => {
    setRange([startYear, endYear]);
    onRangeChange([startYear, endYear]);
  };

  return (
    <>
      <Button size="small" variant="text" onClick={handleClick} sx={{ fontSize: "inherit" }}>
        {range[0]}-{range[1]}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onClose={handleClose}
        PaperProps={{
          elevation: 2,
          sx: { overflow: "visible" },
        }}
        sx={{
          ml: 1,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 2,
            height: 40,
            justifyContent: "space-between",
            paddingLeft: 3,
            paddingRight: 1,
            width: 240,
          }}
        >
          <Slider
            getAriaLabel={() => "Year range"}
            max={endYear}
            min={startYear}
            onChange={handleChange}
            onChangeCommitted={handleChangeCommitted}
            size="small"
            value={range}
            valueLabelDisplay="auto"
          />
          <IconButton
            aria-label="reset range"
            size="small"
            onClick={handleReset}
            disabled={range[0] === startYear && range[1] === endYear}
          >
            <Replay fontSize="small" color="primary" sx={{ transform: "rotate(270deg)" }} />
          </IconButton>
        </Box>
      </Popover>
    </>
  );
};

const BreastCancerDetails = (props: { data: SiteDetails }) => {
  const { data } = props;
  const { distinct_counts_over_time } = data;
  const [selectedYearRange, setSelectedYearRange] = useState<number[]>();
  const sortedHistologies = data.histologies?.sort(
    (a, b) => b.participant_count - a.participant_count
  );

  const handleRangeChange = (range: number[]) => {
    setSelectedYearRange(range);
  };

  return (
    <>
      {/**
       * Title card with count and description
       */}
      <ConceptTitleCard data={data} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: ["1fr", "1fr", "auto auto"],
          columnGap: 2,
          rowGap: 2,
        }}
      >
        <BehaviorCodeChart data={data} />
        <MeasurementChart
          data={data}
          measurementName="Breast/SSF1 - Estrogen Receptor (ER) Assay"
          description="A breakdown of cancer participants by Estrogen Receptor Status."
        />
        <MeasurementChart
          data={data}
          measurementName="Breast/SSF2 - Progesterone Receptor (PR) Assay"
          description="A breakdown of cancer participants by Progesterone Receptor Status."
        />
        <MeasurementChart
          data={data}
          measurementName="Breast/SSF15 - HER2: Summary Result of Testing"
          description="A breakdown of cancer participants by HER2 Receptor Status."
        />
        <MeasurementChart data={data} measurementName="Pathologic Group Stage at Diagnosis" />
      </Box>

      <HistologiesList data={data} />

      {/**
       * Histology & cumulative breakdowns
       */}
      {sortedHistologies && (
        <FlatCard>
          <CardContent className="chart-container">
            <Box display="flex" alignItems="center" gap={0.5} mb={3}>
              <T variant="body1" fontWeight={600}>
                Cumulative Diagnoses
              </T>
              <DiagnosisRangeMenu
                data={distinct_counts_over_time}
                onRangeChange={handleRangeChange}
              />
            </Box>
            <Line
              data={transformCumulativeData(distinct_counts_over_time, selectedYearRange)}
              options={CUMULATIVE_CHART_OPTS}
            />
          </CardContent>
        </FlatCard>
      )}
    </>
  );
};

export default BreastCancerDetails;
